import { generateUUID } from './uids';
// TODO: most of this file can be exported to a common lib

// MW endpoint for channel tracking.
export const CHANNEL_TRACKING_URL = '/apis/channel-tracking';

// impl agnostic for common lib
// eslint-disable-next-line no-undef
const assign = (typeof angular === 'undefined') ? Object.assign : angular.extend;

/**
 * Sends a channel tracking payload.
 * @param {Object} options
 * @param {ChannelTrackingId} options.id Channel tracking action ID
 * @param {Error} [options.error] Error object if this action is an error
 * @param {Object} options.macaroon The user's current macaroon
 * @param {Array<Object>} options.macaroon.accounts The user's account list
 * @param {string} options.macaroon.mainAccountNumber The user's current account number
 * @param {string} options.macaroon.accounts[].accountNumber The account's account number
 * @param {string} options.macaroon.accounts[].guid The account's customer GUID
 * @return {Object} A tracking payload for 5Standards
 */
export const buildChannelPayload = ({
  id,
  interactionType = 'UNKNOWN',
  error,
  legacyAccount = {},
  contextHubAccount = {},
  authGuid,
  contextHubMigratedSource = false,
  isHarness = false,
  metadata = {},
}) => {
  const isContextHubSource = contextHubMigratedSource;
  const fullAccountNumber = isContextHubSource
    ? contextHubAccount.accountNumber
    : legacyAccount.fullAccountNumber;
  const accountGuid = isContextHubSource ? authGuid : legacyAccount.accountGuid;
  const uuid = generateUUID();
  return {
    LogType: 'DSS_CustomerInteraction',
    TransactionId: uuid,
    eventTimestamp: String(Date.now()),
    session: {
      channel: isHarness ? 'MY_ACCOUNT_APP' : 'MY_ACCOUNT_WEB',
      customer: {
        accountNumber: fullAccountNumber,
        custGuid: accountGuid,
      },
    },
    action: {
      id,
      intent: '',
      utterance: null,
      status: (error || id.endsWith('FAILED')) ? 'FAILED' : 'SUCCESS',
      failureReason: error ? error.message : null,
      metadata: assign({ trackingId: uuid }, metadata),
    },
    journeyId: null,
    interactionId: null,
    interactionType,
    contextCode: null,
    disposition: null,
  };
};

export const PAYMENT_START = 'ONE_TIME_PMT_INITIATED';
export const PAYMENT_FAILED = 'ONE_TIME_PMT_FAILED';
export const P2P_FAILED = 'P2P_FAILED';
export const PAYMENT_IMM_COMPLETE = 'IMM_ONE_TIME_PMT_COMPLETED';
export const PAYMENT_SCH_COMPLETE = 'SCH_ONE_TIME_PMT_COMPLETED';
export const P2P_COMPLETED = 'P2P_COMPLETED';
export const P2P_INITIATED = 'P2P_INITIATED';
export const SPLIT_PAY_STARTED = 'SPLIT_PAY_STARTED';
export const SPLIT_PAY_ENROLLED = 'SPLIT_PAY_ENROLLED';
export const SPLIT_PAY_ERROR = 'SPLIT_PAY_ERROR';

/**
 * @typedef {
    typeof PAYMENT_START | typeof PAYMENT_FAILED | typeof P2P_FAILED |
    typeof PAYMENT_IMM_COMPLETE | typeof PAYMENT_SCH_COMPLETE |
    typeof P2P_COMPLETED | typeof P2P_INITIATED |
    typeof SPLIT_PAY_STARTED | typeof SPLIT_PAY_ENROLLED | typeof SPLIT_PAY_ERROR
  } GenericPaymentTrackingId
 */

export const SERVICE_RECOVERY_STARTED = 'SERVICE_RECOVERY_STARTED';
export const SERVICE_RECOVERY_ENROLLED = 'SERVICE_RECOVERY_ENROLLED';
export const SERVICE_RECOVERY_FAILED = 'SERVICE_RECOVERY_FAILED';

/**
 * @typedef {
    typeof SERVICE_RECOVERY_STARTED | typeof SERVICE_RECOVERY_ENROLLED |
    typeof SERVICE_RECOVERY_FAILED
  } SraPaymentTrackingIds
 */

// XM specific channel tracking IDs
export const MOBILE_PAYMENT_START = 'ONE_TIME_MOBILE_PMT_INITIATED';
export const XM_PAYMENT_FAILED = 'XM_ONE_TIME_PMT_FAILED';
export const XM_PAYMENT_IMM_COMPLETE = 'XM_IMM_ONE_TIME_PMT_COMPLETED';

/**
 * @typedef {
    typeof MOBILE_PAYMENT_START | typeof XM_PAYMENT_FAILED |
    typeof XM_PAYMENT_IMM_COMPLETE
  } XmPaymentTrackingIds
 */

// XM Device Payment Channel Tracking IDs
export const XM_DEVICE_PAYMENT_FAILED = 'XM_DEVICE_PAYMENT_FAILED';
export const XM_DEVICE_PAYMENT_IMM_COMPLETE = 'XM_DEVICE_PAYMENT_IMM_COMPLETE';

/**
 * @typedef {
    typeof XM_DEVICE_PAYMENT_FAILED | typeof XM_DEVICE_PAYMENT_IMM_COMPLETE
  } XmDevicePaymentTrackingId
 */

export const ECOBILL_ENROLL_FAILED = 'ECO_ENROLL_FAILED';
export const ECOBILL_UNENROLL_FAILED = 'ECO_UNENROLL_FAILED';
export const ECOBILL_ENROLL_COMPLETE = 'ECO_ENROLL_COMPLETE';
export const ECOBILL_UNENROLL_COMPLETE = 'ECO_UNENROLL_COMPLETE';

/**
 * @typedef {
    typeof ECOBILL_ENROLL_FAILED | typeof ECOBILL_UNENROLL_FAILED |
    typeof ECOBILL_ENROLL_COMPLETE | typeof ECOBILL_UNENROLL_COMPLETE
  } EcobillTrackingId
 */

export const BILLING_STMT_ACK = 'STMT_ACK';

export const AUTOPAY_ENROLL_COMPLETE = 'PAYMENT_AUTO_ENROLL_COMPLETE';
export const AUTOPAY_UNENROLL_COMPLETE = 'PAYMENT_AUTO_UNENROLL_COMPLETE';
export const AUTOPAY_ENROLL_FAILED = 'PAYMENT_AUTO_ENROLL_FAILED';
export const AUTOPAY_UNENROLL_FAILED = 'PAYMENT_AUTO_UNENROLL_FAILED';

/**
 * @typedef {
    typeof AUTOPAY_ENROLL_COMPLETE | typeof AUTOPAY_UNENROLL_COMPLETE |
    typeof AUTOPAY_ENROLL_FAILED | typeof AUTOPAY_UNENROLL_FAILED
  } AutopayTrackingId
 */

export const XFINITY_ASSISTANCE_PLAN_INITIATED = 'XFINITY_ASSISTANCE_PLAN_INITIATED';
export const XFINITY_ASSISTANCE_PLAN_COMPLETED = 'XFINITY_ASSISTANCE_PLAN_COMPLETED';
export const XFINITY_ASSISTANCE_PLAN_FAILED = 'XFINITY_ASSISTANCE_PLAN_FAILED';

/**
 * @typedef {
    typeof XFINITY_ASSISTANCE_PLAN_INITIATED | typeof XFINITY_ASSISTANCE_PLAN_COMPLETED |
    typeof XFINITY_ASSISTANCE_PLAN_FAILED
  } XfinityAssistancePlanTrackingId
 */

export const LTIP_INITIATED = 'LTIP_INITIATED';
export const LTIP_COMPLETED = 'LTIP_COMPLETED';
export const LTIP_FAILED = 'LTIP_FAILED';

/**
 * @typedef {(typeof LTIP_INITIATED | typeof LTIP_COMPLETED | typeof LTIP_FAILED)} LtipTrackingId
 */

/**
 * @typedef {
    GenericPaymentTrackingId | XmPaymentTrackingIds | XmDevicePaymentTrackingId |
    EcobillTrackingId | AutopayTrackingId | XfinityAssistancePlanTrackingId |
    LtipTrackingId | SraPaymentTrackingIds | typeof BILLING_STMT_ACK
  } ChannelTrackingId
 */
